import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled, { keyframes } from 'styled-components'
import { useTranslation } from 'react-i18next'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import { getFiltersQuery } from 'utils/filters'
import { isNotNilOrEmpty } from 'utils/ramda'
import { not, values, equals, isEmpty, pathOr } from 'ramda'
import Drawer from '@mui/material/Drawer'
import FilterListIcon from '@mui/icons-material/FilterList'
import { selectIsFetchingBps } from 'ducks/loaderSelectors'
import Skeleton from '@mui/material/Skeleton'
import {
  getUnitsCount,
  getUnitsPagesCount,
  selectUserUnits
} from 'ducks/units/selectors'
import { fetchUserUnitsRoutine } from 'ducks/units/actions'
import UnitFilters from 'features/filters/UnitFilters'
import EmptyUnitsListState from 'features/bpList/Components/EmptyUnitsListState'
import { useNavigate } from 'react-router-dom'
import { formatDate } from 'utils/date'
import { getOwner } from 'utils/hooks/values'

const defaultFilters = {
  owner: [],
  responsible_person: [],
  created_at: null,
  updated_at: null,
  building_types: []
}

const UnitsList = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const units = useSelector(selectUserUnits)
  const pagesCount = useSelector(getUnitsPagesCount)
  const totalFound = useSelector(getUnitsCount)
  const [isDrawerOpen, setDrawerOpen] = useState(false)
  const [filters, setFilters] = useState(defaultFilters)
  const isLoading = useSelector(selectIsFetchingBps)
  const [sortingOptions, setSortingOptions] = useState({
    column: 'name',
    desc: false,
    page: 1
  })

  const prevRequest = useRef({ sortingOptions: null, filters: null })
  const initialLoadComplete = useRef(false)

  const filtersQuery = useMemo(() => getFiltersQuery(filters), [filters])

  useEffect(() => {
    const { page, desc, column } = sortingOptions
    const sortParam = desc ? `-${column}` : column

    const isDifferent =
      !prevRequest.current.sortingOptions ||
      !prevRequest.current.filters ||
      prevRequest.current.sortingOptions.page !== page ||
      prevRequest.current.sortingOptions.desc !== desc ||
      prevRequest.current.sortingOptions.column !== column ||
      prevRequest.current.filters !== filtersQuery

    if (isDifferent || !initialLoadComplete.current) {
      dispatch(
        fetchUserUnitsRoutine({
          sort: sortParam,
          filters: filtersQuery,
          page
        })
      )

      prevRequest.current = {
        sortingOptions: { ...sortingOptions },
        filters: filtersQuery
      }
      initialLoadComplete.current = true
    }
  }, [sortingOptions, filtersQuery, dispatch])

  const hasMore = sortingOptions.page < pagesCount
  const loadMore = useCallback(() => {
    setSortingOptions(prev => ({ ...prev, page: prev.page + 1 }))
  }, [])

  const handleSetFilters = useCallback(next => {
    setFilters(next)
    setSortingOptions(prev => (prev.page === 1 ? prev : { ...prev, page: 1 }))
  }, [])

  const openDrawer = useCallback(() => setDrawerOpen(true), [])
  const closeDrawer = useCallback(() => setDrawerOpen(false), [])
  const handleClearFilters = useCallback(() => handleSetFilters(defaultFilters), [handleSetFilters])

  const shouldRenderClearButton = useMemo(() => {
    return (
      values(filters).some(e => isNotNilOrEmpty(e)) &&
      not(equals(filters, defaultFilters))
    )
  }, [filters])

  const handleCardClick = unit => {
    if (unit.buildingPassportId && unit.id) {
      // navigate(`/bp-list/${unit.buildingPassportId}/units/`)
      navigate(`/bp-list/${unit.buildingPassportId}/units/${unit.id}`)
    }
  }

  const getStatusColor = status => {
    switch (status?.toLowerCase()) {
      case 'active':
        return { bg: '#e6f7ff', text: '#1890ff' }
      case 'inactive':
        return { bg: '#fff1f0', text: '#f5222d' }
      default:
        return { bg: '#f5f5f5', text: '#8c8c8c' }
    }
  }

  const hasServerResults = !isEmpty(units)
  const UnitCard = ({ unit }) => {
    const statusStyle = unit.status ? getStatusColor(unit.status) : null

    const unitUprn = pathOr('Not yet allocated', ['uprn'], unit)
    const bpUprn = pathOr(null, ['buildingPassport', 'uprn'], unit)

    const street = pathOr('---', ['buildingPassport', 'street'], unit)
    const city = pathOr('---', ['buildingPassport', 'city'], unit)
    const address = `${street}, ${city}`

    const postcode = pathOr('---', ['buildingPassport', 'postalCode'], unit)
    const owner = pathOr(null, ['owners', 0], unit)
      ? getOwner(unit.owners[0])
      : '---'

    return (
      <UnifiedCard onClick={() => handleCardClick(unit)}>
        <CardHeader>
          <CardTitle>{unit.name}</CardTitle>
          {statusStyle && (
            <StatusBadgeMobile bg={statusStyle.bg} text={statusStyle.text}>
              {unit.status}
            </StatusBadgeMobile>
          )}
        </CardHeader>

        <CardBody>
          <CardRow>
            <Label>UPRN</Label>
            <Value
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end'
              }}
            >
              <span style={{ fontWeight: 600 }}>{unitUprn}</span>
              {bpUprn && bpUprn !== 'Not yet allocated' && (
                <span
                  style={{
                    fontSize: '11px',
                    color: '#999',
                    marginTop: '2px'
                  }}
                >
                  Building Passport: {bpUprn}
                </span>
              )}
            </Value>
          </CardRow>

          {unit.plotNumber && (
            <CardRow>
              <Label>Plot</Label>
              <Value>{unit.plotNumber}</Value>
            </CardRow>
          )}

          <CardRow>
            <Label>Address</Label>
            <Value>{address}</Value>
          </CardRow>

          <CardRow>
            <Label>Postcode</Label>
            <Value>{postcode}</Value>
          </CardRow>

          <CardRow>
            <Label>Unit Owner</Label>
            <Value>{owner}</Value>
          </CardRow>

          <CardRow>
            <Label>Created</Label>
            <Value>{formatDate(unit.createdAt)}</Value>
          </CardRow>

          <CardRow>
            <Label>Updated</Label>
            <Value>{formatDate(unit.updatedAt)}</Value>
          </CardRow>
        </CardBody>
      </UnifiedCard>
    )
  }

  return (
    <PageWrapper>
      <ControlsRow>
        <ResultsInfo>
          {totalFound > 0 && hasServerResults && (
            <ResultsCount>
              <span style={{ fontWeight: 'bold' }}>{units.length}</span>
              <span style={{ marginLeft: '5px' }}>
                of {totalFound} {totalFound === 1 ? 'unit' : 'units'}
              </span>
            </ResultsCount>
          )}
        </ResultsInfo>
        <ActionsGroup>
          {shouldRenderClearButton && (
            <ClearButton onClick={handleClearFilters}>
              {t('bpList.clearFilters')}
            </ClearButton>
          )}
          <FilterButton onClick={openDrawer}>
            <FiltersIcon />
            <FilterText>Filters</FilterText>
          </FilterButton>
        </ActionsGroup>
      </ControlsRow>

      <ContentArea>
        {isLoading && !hasServerResults ? (
          <>
            <MobileView>
              <CardsGrid>
                {[...Array(4)].map((_, i) => (
                  <SkeletonCard key={i}>
                    <SkeletonCardHeader>
                      <Skeleton variant="text" width="55%" height={24} />
                      <Skeleton variant="rounded" width={60} height={22} sx={{ borderRadius: '4px', ml: 'auto' }} />
                    </SkeletonCardHeader>
                    <SkeletonCardBody>
                      {[...Array(7)].map((_, j) => (
                        <SkeletonRow key={j}>
                          <Skeleton variant="text" width={80} height={18} />
                          <Skeleton variant="text" width="40%" height={18} />
                        </SkeletonRow>
                      ))}
                    </SkeletonCardBody>
                  </SkeletonCard>
                ))}
              </CardsGrid>
            </MobileView>
            <DesktopView>
              <TableContainer>
                <StyledTable>
                  <thead>
                    <tr>
                      <th>Unit Name</th>
                      <th>UPRN</th>
                      <th>Plot</th>
                      <th>Address</th>
                      <th>Postcode</th>
                      <th>Unit Owner</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...Array(6)].map((_, i) => (
                      <tr key={i} style={{ cursor: 'default' }}>
                        <td><Skeleton variant="text" width="80%" height={20} /></td>
                        <td><Skeleton variant="text" width="70%" height={20} /></td>
                        <td><Skeleton variant="text" width="50%" height={20} /></td>
                        <td><Skeleton variant="text" width="75%" height={20} /></td>
                        <td><Skeleton variant="text" width="65%" height={20} /></td>
                        <td><Skeleton variant="text" width="60%" height={20} /></td>
                        <td><Skeleton variant="rounded" width={70} height={24} sx={{ borderRadius: '12px' }} /></td>
                        <td><Skeleton variant="text" width="60%" height={20} /></td>
                        <td></td>
                      </tr>
                    ))}
                  </tbody>
                </StyledTable>
              </TableContainer>
            </DesktopView>
          </>
        ) : isEmpty(units) && equals(defaultFilters, filters) ? (
          <EmptyUnitsListState />
        ) : !hasServerResults ? (
          <NoSearchResults>No units found.</NoSearchResults>
        ) : (
          <>
            <MobileView>
              <CardsGrid>
                {units.map(unit => (
                  <UnitCard key={unit.id} unit={unit} />
                ))}
              </CardsGrid>
            </MobileView>
            <DesktopView>
              <TableContainer>
                <StyledTable>
                  <thead>
                  <tr>
                    <th>Unit Name</th>
                    <th>UPRN</th>
                    <th>Plot</th>
                    <th>Address</th>
                    <th>Postcode</th>
                    <th>Unit Owner</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th></th>
                  </tr>
                  </thead>
                  <tbody>
                  {units.map(unit => {
                    const statusColors = unit.status ? getStatusColor(unit.status) : null
                    const owner = unit.owners?.[0] ? getOwner(unit.owners[0]) : '-'
                    const address = [unit.buildingPassport?.street, unit.buildingPassport?.city]
                      .filter(Boolean)
                      .join(', ') || '-'

                    return (
                      <tr key={unit.id} onClick={() => handleCardClick(unit)}>
                        <td>
                          <TableName>{unit.name}</TableName>
                        </td>
                        <td>{unit.uprn || '-'}</td>
                        <td>{unit.plotNumber || '-'}</td>
                        <td>{address}</td>
                        <td>{unit.buildingPassport?.postalCode || '-'}</td>
                        <td>{owner}</td>
                        <td>
                          {statusColors && (
                            <TableStatusBadge $bg={statusColors.bg} $text={statusColors.text}>
                              {unit.status}
                            </TableStatusBadge>
                          )}
                        </td>
                        <td>{formatDate(unit.createdAt)}</td>
                        <td style={{ textAlign: 'right' }}>
                          <ArrowIcon>→</ArrowIcon>
                        </td>
                      </tr>
                    )
                  })}
                  </tbody>
                </StyledTable>
              </TableContainer>
            </DesktopView>
          </>
        )}
        {hasServerResults && (
          <InfiniteScrollSentinel
            hasMore={hasMore}
            loading={isLoading}
            onLoadMore={loadMore}
            label="Loading more Unit Passports…"
          />
        )}
      </ContentArea>

      <Drawer
        anchor='right'
        variant='persistent'
        open={isDrawerOpen}
        onClose={closeDrawer}
        style={{ zIndex: 1200 }}
        PaperProps={{
          sx: {
            borderTopLeftRadius: '12px',
            borderBottomLeftRadius: '12px',
            boxShadow: '-5px 0 25px rgba(0, 0, 0, 0.1)',
            width: '350px',
            maxWidth: '90vw'
          }
        }}
      >
        <FiltersWrapper>
          <FiltersHeader>
            <FiltersTitle>Filter Units</FiltersTitle>
            <CloseButton onClick={closeDrawer}>×</CloseButton>
          </FiltersHeader>
          <FiltersContent>
            <UnitFilters
              closeDrawer={closeDrawer}
              parentFilters={filters}
              getFilters={handleSetFilters}
              defaultFilters={defaultFilters}
            />
          </FiltersContent>
        </FiltersWrapper>
      </Drawer>
    </PageWrapper>
  )
}

export default UnitsList

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`

const PageWrapper = styled.div`
  width: 100%;
  animation: ${fadeIn} 0.3s ease-out;
`

const MobileView = styled.div`
  display: none;
  @media (max-width: 1024px) {
    display: block;
  }
`

const DesktopView = styled.div`
  display: block;
  @media (max-width: 1024px) {
    display: none;
  }
`

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.03);
  overflow: hidden;
  width: 100%;
`

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;

  thead {
    background-color: #f9fafb;
    border-bottom: 1px solid #e5e7eb;

    th {
      padding: 16px 24px;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      color: #6b7280;
      letter-spacing: 0.05em;
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #f3f4f6;
      transition: all 0.2s;
      cursor: pointer;

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        background-color: #f9fafb;

        td:first-child span {
          color: ${({ theme }) => theme.colors.primary};
        }
      }

      td {
        padding: 16px 24px;
        font-size: 14px;
        color: #111827;
        vertical-align: middle;
      }
    }
  }
`

const TableName = styled.span`
  font-weight: 600;
  color: #110c2f;
  transition: color 0.2s;
`

const TableStatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${props => props.$bg || '#f3f4f6'};
  color: ${props => props.$text || '#374151'};
`

const ArrowIcon = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: bold;
  font-size: 18px;
`

const ControlsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    align-items: center;
  }
`

const ResultsInfo = styled.div`
  display: flex;
  align-items: center;
`

const ResultsCount = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 14px;
  background-color: #f5f7fa;
  padding: 8px 16px;
  border-radius: 8px;
`

const ActionsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;

  @media (max-width: 768px) {
    order: 2;
  }
`

const ClearButton = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13px;
  font-weight: 500;
  text-transform: uppercase;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.primary}10;
  }

  @media (max-width: 480px) {
    padding: 8px 10px;
    font-size: 11px;
  }
`

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px ${({ theme }) => theme.colors.primary}30;

  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
    transform: translateY(-2px);
  }
`

const FilterText = styled.span`
  margin-left: 8px;
  font-weight: 500;
  @media (max-width: 480px) {
    display: none;
  }
`

const FiltersIcon = styled(FilterListIcon)`
  font-size: 18px !important;
`

const ContentArea = styled.div`
  width: 100%;
`
const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  width: 100%;
  padding: 16px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    padding: 0;
    gap: 16px;
  }
`

const UnifiedCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  border: 1px solid rgba(0,0,0,0.05);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    border-color: ${({ theme }) => theme.colors.primary}30;
  }

  &:active {
    transform: scale(0.98);
  }
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
`

const CardTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  line-height: 1.3;
`

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const CardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  font-size: 14px;
`

const Label = styled.span`
  color: ${({ theme }) => theme.colors.grey[600]};
  font-size: 13px;
  text-transform: uppercase;
  font-weight: 500;
  min-width: 100px;
`

const Value = styled.span`
  color: ${({ theme }) => theme.colors.haiti};
  font-weight: 500;
  text-align: right;
  flex: 1;
  word-break: break-word;
`

const StatusBadgeMobile = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background-color: ${({ bg }) => bg};
  color: ${({ text }) => text};
  white-space: nowrap;
`

const SkeletonCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
`

const SkeletonCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
`

const SkeletonCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const SkeletonRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const NoSearchResults = styled.div`
  margin-top: 40px;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey[700]};
  font-size: 14px;
`

const FiltersContent = styled.div`
  padding: 0 20px;
`

const FiltersWrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: white;
  overflow-y: auto;
  position: relative;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.primary}40;
    border-radius: 4px;
  }
`

const FiltersHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lighterGrey};
`

const FiltersTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  color: ${({ theme }) => theme.colors.haiti};
  font-weight: 600;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;

  &:hover {
    background-color: ${({ theme }) => theme.colors.lighterGrey};
  }
`
